// Geração de PDF da cotação rápida + upload para Storage.
// Padrão alinhado com src/lib/proposal-pdf.ts (mas em arquivo separado
// para não tocar no fluxo de propostas existente).

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export interface QuotePdfResult {
  blob: Blob;
  filename: string;
}

/**
 * Captura cada filho do `element` como uma página A4 e gera um PDF.
 * - mode === "download": chama doc.save() e retorna void.
 * - mode === "blob": devolve { blob, filename } para upload posterior.
 *
 * O elemento deve conter um ou mais filhos com largura 794px (A4 @ 96dpi).
 */
export async function generateQuotePdf(
  element: HTMLElement,
  quoteCode: string,
  mode: "download" | "blob" = "download",
): Promise<QuotePdfResult | void> {
  const filename = `cotacao-${quoteCode.toLowerCase()}.pdf`;

  // Posicionar fora da tela para evitar flash visual durante a captura.
  const original = {
    position: element.style.position,
    left: element.style.left,
    top: element.style.top,
    zIndex: element.style.zIndex,
    visibility: element.style.visibility,
  };
  element.style.position = "absolute";
  element.style.left = "0px";
  element.style.top = "0px";
  element.style.zIndex = "-9999";
  element.style.visibility = "visible";

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const A4_W = 210;
  const A4_H = 297;
  const PAGE_PX_W = 794;
  const PAGE_PX_H = 1123;

  try {
    const pages = Array.from(element.children) as HTMLElement[];
    if (pages.length === 0) {
      throw new Error("Template sem páginas para renderizar");
    }

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      if (i > 0) doc.addPage();

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: PAGE_PX_W,
        height: Math.max(page.scrollHeight, PAGE_PX_H),
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const imgH = (canvas.height / canvas.width) * A4_W;
      doc.addImage(imgData, "JPEG", 0, 0, A4_W, Math.min(imgH, A4_H));
    }
  } finally {
    element.style.position = original.position;
    element.style.left = original.left;
    element.style.top = original.top;
    element.style.zIndex = original.zIndex;
    element.style.visibility = original.visibility;
  }

  if (mode === "download") {
    doc.save(filename);
    return;
  }

  const blob = doc.output("blob");
  return { blob, filename };
}

/**
 * Sobe o PDF no bucket público `quote-pdfs` em
 * `{tenantId}/{filename}` e devolve a URL pública.
 */
export async function uploadQuotePdf(
  blob: Blob,
  filename: string,
  tenantId: string,
): Promise<string> {
  const { supabase } = await import("@/integrations/supabase/client");

  const path = `${tenantId}/${filename}`;
  const { error } = await supabase.storage
    .from("quote-pdfs")
    .upload(path, blob, {
      contentType: "application/pdf",
      upsert: true,
    });
  if (error) throw error;

  const { data } = supabase.storage.from("quote-pdfs").getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Converte um Blob para string base64 (sem prefixo data:).
 * Faz a conversão em chunks para evitar stack overflow em PDFs grandes.
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(binary);
}
